import { messageError } from "reusable/mui/modals/message-error"
import { decodeTime, ulid } from "ulid"
import { MimeType, FileExtension } from '@gap/utils/file/file-utils'

export default async function fileToBase64(file: File, message = true) {
   return new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = error => {
         if (message) {
            messageError(`File could not be read: ${error}`)
         }
         reject(error)
      }
      reader.readAsDataURL(file)
   })
}

export async function fileToArrayBuffer(file: File, message = true) {
   return new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as ArrayBuffer)
      reader.onerror = error => {
         if (message) {
            messageError(`File could not be read: ${error}`)
         }
         reject(error)
      }
      reader.readAsArrayBuffer(file)
   })
}

export const base64StringToBlob = (b64Data: string, contentType = '', sliceSize = 512) => {
   const byteCharacters = atob(b64Data.replace(/^data:(.+);base64,/, ''))
   const byteArrays = []

   for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize)

      const byteNumbers = new Array(slice.length)
      for (let i = 0; i < slice.length; i++) {
         byteNumbers[i] = slice.charCodeAt(i)
      }

      const byteArray = new Uint8Array(byteNumbers)
      byteArrays.push(byteArray)
   }

   const blob = new Blob(byteArrays, { type: contentType })
   return blob
}

export const appendUlidToFile = (file: File): File => {
   const [, fileName, fileExtension] = /^(.+)\.(.+)$/gi.exec(file.name) || []
   const newFilename = `${fileName}-${ulid()}.${fileExtension}`
   return renameFile(file, newFilename)
}

/**
 * Rename a given file with the given new filename
 */
export const renameFile = (file: File, newFilename: string, options?: FilePropertyBag): File => {
   const blob = file.slice(0, file.size, file.type)
   return new File([blob], newFilename, { type: file.type, ...options })
}

const fileNameForbiddenCharaters: Record<string, `%${string}`> = {
   // Character apple likes to includes in screenshots. It's like a space, looks like a space, acts like a space but will mess up your day
   '%E2%80%AF': '%20'
}

/**
 * This function will format a file to not include breaking characters
 * @param file File
 * @param options options for the renaming
 * @returns A file with a safe name
 */
export const formatFile = (file: File, options?: { appendUlid: boolean }): File => {
   let fileName = encodeURI(file.name)

   for (const [forbiddenCharacter, replaceValue] of Object.entries(fileNameForbiddenCharaters)) {
      fileName = fileName.replaceAll(forbiddenCharacter, replaceValue)
   }

   if (options?.appendUlid) {
      fileName = encodeURI(appendUlidToFile(new File([file], decodeURI(fileName))).name)
   }

   return new File([file], decodeURI(fileName), {
      ...(fileName.endsWith('.svg') ? {
         type: 'image/svg+xml'
      } : {})
   });
}

/**
 * Quick way of simulating a file if you only have the filename
 */
export const stringToFile = (filename: string): File => {
   return new File([], filename)
}

/**
 * Extract ulid from filename and convert to date
 * Return file and format name without the ulid part
 * Return ulid as the id
 */
export const decodeFile = (file: File | string): { file: File, created: Date, id: string, originalFile: File, extension: FileExtension, fileName: string } => {
   if (typeof file === 'string') {
      file = stringToFile(file)
   }

   // Split file into parts, e.g. 'my_file-one-01HFSJ7JS77C2RE7N25068X7HA.pdf' -> 'my_file-one', '01HFSJ7JS77C2RE7N25068X7HA', 'pdf'
   const [, originalNamePart, ulidPart, extension] = /^(.+)-([0-9a-z]{26})\.(.+)$/gi.exec(file.name) || []

   // Not every filename carries our appended ULID (e.g. externally-named attachments). Without one we
   // can't derive a creation time, so fall back to the raw filename instead of crashing in decodeTime.
   if (!ulidPart) {
      const lastDotIndex = file.name.lastIndexOf('.')
      return {
         file,
         created: new Date(file.lastModified || 0),
         id: '',
         originalFile: file,
         extension: getExtensionFromFile(file),
         fileName: lastDotIndex > 0 ? file.name.substring(0, lastDotIndex) : file.name
      }
   }

   const createdEpochTime = decodeTime(ulidPart)

   const created = new Date(createdEpochTime)

   const renamedFile = renameFile(file, `${originalNamePart}.${extension}`, { lastModified: createdEpochTime })

   // Set correct date on the original file
   const originalFile = renameFile(file, file.name, { lastModified: createdEpochTime })
   return {
      file: renamedFile,
      created,
      id: ulidPart,
      originalFile,
      extension: getExtensionFromFile(originalFile),
      fileName: originalNamePart
   }
}

/**
 * Get the extension of a given file
 * @param file File
 * @returns file extension (ex. png)
 */
export const getExtensionFromFile = (file: File): FileExtension => {
   const fileName = file.name
   return getExtensionFromFileName(fileName)
}

export const getExtensionFromFileName = (fileName: string): FileExtension => {
   return (fileName.substring(fileName.lastIndexOf('.') + 1, fileName.length) || fileName).toLowerCase() as FileExtension
}

export const fileIsAGapImage = (file: File): boolean => {
   const extension = getExtensionFromFile(file)
   switch (extension) {
      case 'png':
      case 'jpg':
      case 'jpeg':
         return true
      default:
         return false
   }
}

export const extensionToMimeType = (extension: FileExtension): MimeType => {
   switch (extension) {
      case 'jpg':
      case 'jpeg':
         return "image/jpeg"
      case 'png':
         return "image/png"
      default:
         throw new Error(`Not yet supported extenstion ${extension}`)
   }
}

export const srcToFile = async (fileSrc: string, fileName: string): Promise<File> => {
   const extension = getExtensionFromFileName(fileName)
   const response = await fetch(fileSrc)
   const data = await response.blob()
   const metadata = {
      type: extensionToMimeType(extension)
   }
   return new File([data], fileName, metadata)
}
